import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../utils/api';

const HIGH_RISK_THRESHOLD = 3;

export default function RiskAnalysis() {
  const [statsLoading, setStatsLoading] = useState(true);
  const [cycles, setCycles] = useState([]);
  const [cycleFilter, setCycleFilter] = useState('');

  const [tableLoading, setTableLoading] = useState(true);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [minScore, setMinScore] = useState('');
  const [error, setError] = useState('');
  const [riskRulesOpen, setRiskRulesOpen] = useState(false);
  const [riskRulesLoading, setRiskRulesLoading] = useState(false);
  const [riskRulesSaving, setRiskRulesSaving] = useState(false);
  const [riskRules, setRiskRules] = useState([]);
  const [riskDetailOpen, setRiskDetailOpen] = useState(false);
  const [riskDetail, setRiskDetail] = useState(null);
  const [riskDetailLoading, setRiskDetailLoading] = useState(false);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const params = {};
      if (cycleFilter) params.cycle = cycleFilter;
      const response = await axiosInstance.get('/api/admin/dashboard/stats/', { params });
      setCycles(response.data.cycles.all || []);
    } catch (err) {
      console.error('Failed to load cycles:', err);
      setError('Failed to load cycles');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchQuestionnaires = async () => {
    try {
      setTableLoading(true);
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        status: 'completed',
      };
      if (cycleFilter) params.cycle = cycleFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await axiosInstance.get('/api/admin/dashboard/questionnaires/', { params });
      setQuestionnaires(response.data.data);
      setTotalCount(response.data.pagination.total_count);
    } catch (err) {
      console.error('Failed to load questionnaires:', err);
      setError('Failed to load questionnaires');
    } finally {
      setTableLoading(false);
    }
  };

  const handleOpenRiskRules = async () => {
    try {
      setRiskRulesLoading(true);
      const response = await axiosInstance.get('/api/admin/dashboard/risk-rules/');
      setRiskRules(response.data.rules || []);
      setRiskRulesOpen(true);
    } catch (err) {
      console.error('Failed to load risk rules:', err);
      setError(err.response?.data?.error || 'Failed to load risk rules');
    } finally {
      setRiskRulesLoading(false);
    }
  };

  const handleSaveRiskRules = async () => {
    try {
      setRiskRulesSaving(true);
      const payload = {
        rules: riskRules.map((rule, index) => ({
          id: rule.id,
          label: rule.label,
          description: rule.description,
          severity: rule.severity,
          weight: Number(rule.weight || 0),
          enabled: rule.enabled,
          order: index * 10,
        }))
      };
      await axiosInstance.put('/api/admin/dashboard/risk-rules/', payload);
      setRiskRulesOpen(false);
      fetchQuestionnaires();
    } catch (err) {
      console.error('Failed to save risk rules:', err);
      setError(err.response?.data?.error || 'Failed to save risk rules');
    } finally {
      setRiskRulesSaving(false);
    }
  };

  const handleExportRisk = async () => {
    try {
      const params = {};
      if (cycleFilter) params.cycle = cycleFilter;
      if (searchQuery) params.search = searchQuery;
      if (minScore) params.min_score = minScore;

      const response = await axiosInstance.get('/api/admin/dashboard/risk-export/', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RiskAnalysis_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export risk analysis:', err);
      setError(err.response?.data?.error || 'Failed to export risk analysis');
    }
  };

  useEffect(() => {
    fetchStats();
  }, [cycleFilter]);

  useEffect(() => {
    fetchQuestionnaires();
  }, [page, rowsPerPage, searchQuery, cycleFilter]);

  const filteredRows = useMemo(() => {
    if (!minScore) return questionnaires;
    const threshold = Number(minScore);
    return questionnaires.filter((q) => (q.risk_score || 0) >= threshold);
  }, [questionnaires, minScore]);

  const summary = useMemo(() => {
    const scores = questionnaires.map((q) => q.risk_score || 0);
    const total = scores.length;
    const highRisk = scores.filter((s) => s >= HIGH_RISK_THRESHOLD).length;
    const avgScore = total ? Math.round((scores.reduce((a, b) => a + b, 0) / total) * 10) / 10 : 0;
    const flagged = questionnaires.filter((q) => (q.risk_flags || []).length > 0).length;
    return { total, highRisk, avgScore, flagged };
  }, [questionnaires]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Risk Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Completed questionnaires only. Risk scores come from the active rule set.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {statsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h4">{summary.total}</Typography>
                <Typography variant="body2" color="text.secondary">Completed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h4">{summary.highRisk}</Typography>
                <Typography variant="body2" color="text.secondary">High Risk (≥ {HIGH_RISK_THRESHOLD})</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h4">{summary.avgScore}</Typography>
                <Typography variant="body2" color="text.secondary">Average Score</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h4">{summary.flagged}</Typography>
                <Typography variant="body2" color="text.secondary">Flagged Properties</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Risk Table
          </Typography>

          <TextField
            size="small"
            placeholder="Search by email, name, or property..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Cycle</InputLabel>
            <Select
              value={cycleFilter}
              label="Cycle"
              onChange={(e) => { setCycleFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Cycles</MenuItem>
              {cycles.map((cycle) => (
                <MenuItem key={cycle.id} value={cycle.id}>
                  {new Date(cycle.date_opened).toLocaleDateString()}
                  {cycle.date_closed ? ` - ${new Date(cycle.date_closed).toLocaleDateString()}` : ' (Open)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Min Score</InputLabel>
            <Select
              value={minScore}
              label="Min Score"
              onChange={(e) => setMinScore(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">1+</MenuItem>
              <MenuItem value="2">2+</MenuItem>
              <MenuItem value="3">3+</MenuItem>
              <MenuItem value="4">4+</MenuItem>
              <MenuItem value="5">5+</MenuItem>
            </Select>
          </FormControl>

          <Button variant="outlined" onClick={handleOpenRiskRules}>
            Risk Rules
          </Button>

          <Button variant="contained" onClick={handleExportRisk}>
            Export Excel
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Property Name</TableCell>
                <TableCell>User Email</TableCell>
                <TableCell>Risk Score</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No completed questionnaires found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice()
                  .sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0))
                  .map((q) => (
                    <TableRow key={q.id} hover>
                      <TableCell>{q.property_name}</TableCell>
                      <TableCell>{q.user_email}</TableCell>
                      <TableCell>
                        <Tooltip title="Risk score = sum of weights for triggered rules">
                          <Chip
                            label={q.risk_score ? `Score ${q.risk_score}` : 'None'}
                            color={q.risk_score ? 'warning' : 'default'}
                            size="small"
                            icon={q.risk_score ? <WarningIcon /> : <CheckCircleIcon />}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={q.risk_flags?.length ? q.risk_flags.join(', ') : 'No risk flags'}>
                          <Typography variant="body2">
                            {q.risk_flags?.length ? q.risk_flags.length : 0}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {q.date_completed ? new Date(q.date_completed).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setRiskDetailOpen(true);
                            setRiskDetailLoading(true);
                            setRiskDetail(null);
                            axiosInstance.get('/api/admin/dashboard/questionnaire-detail/', {
                              params: { questionnaire_id: q.id }
                            }).then((response) => {
                              setRiskDetail(response.data);
                            }).catch((err) => {
                              console.error('Failed to load questionnaire detail:', err);
                              setError(err.response?.data?.error || 'Failed to load questionnaire detail');
                            }).finally(() => {
                              setRiskDetailLoading(false);
                            });
                          }}
                        >
                          View Risk
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Risk Rules Dialog */}
      <Dialog open={riskRulesOpen} onClose={() => setRiskRulesOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>Risk Rules</DialogTitle>
        <DialogContent>
          <TableContainer sx={{ maxHeight: 520 }}>
          {riskRulesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enable/disable rules and adjust severity and weight. Logic is fixed; these settings control scoring and visibility.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Enabled</TableCell>
                    <TableCell sx={{ width: 220 }}>Label</TableCell>
                    <TableCell sx={{ width: 360 }}>Description</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell sx={{ width: 360 }}>Fields</TableCell>
                    <TableCell sx={{ width: 120 }}>Weight</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {riskRules.map((rule, idx) => (
                    <TableRow key={rule.id || rule.key}>
                      <TableCell>
                        <Switch
                          checked={!!rule.enabled}
                          onChange={(e) => {
                            const updated = [...riskRules];
                            updated[idx] = { ...rule, enabled: e.target.checked };
                            setRiskRules(updated);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={rule.label}
                          onChange={(e) => {
                            const updated = [...riskRules];
                            updated[idx] = { ...rule, label: e.target.value };
                            setRiskRules(updated);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={rule.description || ''}
                          onChange={(e) => {
                            const updated = [...riskRules];
                            updated[idx] = { ...rule, description: e.target.value };
                            setRiskRules(updated);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={rule.severity}
                          onChange={(e) => {
                            const updated = [...riskRules];
                            updated[idx] = { ...rule, severity: e.target.value };
                            setRiskRules(updated);
                          }}
                        >
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {(rule.fields || []).length === 0 ? (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          ) : (
                            rule.fields.map((field) => (
                              <Tooltip
                                key={`${field.section}.${field.field}`}
                                title={`${field.section}.${field.field}`}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  {field.section_label}: {field.field_label}
                                </Typography>
                              </Tooltip>
                            ))
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, step: 1 }}
                          value={rule.weight}
                          onChange={(e) => {
                            const updated = [...riskRules];
                            updated[idx] = { ...rule, weight: e.target.value };
                            setRiskRules(updated);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRiskRulesOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveRiskRules} variant="contained" disabled={riskRulesSaving}>
            {riskRulesSaving ? 'Saving...' : 'Save Rules'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Risk Detail Dialog */}
      <Dialog open={riskDetailOpen} onClose={() => setRiskDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Risk Details</DialogTitle>
        <DialogContent>
          {riskDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : riskDetail ? (
            <>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {riskDetail.property_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {riskDetail.user_email}
              </Typography>
              <Chip
                label={riskDetail.risk_score ? `Score ${riskDetail.risk_score}` : 'No Score'}
                color={riskDetail.risk_score ? 'warning' : 'default'}
                size="small"
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" sx={{ mb: 1 }}>
                Flags
              </Typography>
              {riskDetail.risk_flags.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No risk flags for this property.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {riskDetail.risk_flags.map((flag) => (
                    <Chip key={flag} label={flag} size="small" color="error" variant="outlined" />
                  ))}
                </Box>
              )}

              <Typography variant="body2" sx={{ mb: 1 }}>
                Questionnaire Preview
              </Typography>
              {Object.entries(riskDetail.sections || {}).map(([sectionKey, fields]) => (
                <Box key={sectionKey} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize' }}>
                    {sectionKey.replace(/_/g, ' ')}
                  </Typography>
                  {!fields ? (
                    <Typography variant="body2" color="text.secondary">
                      No data
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableBody>
                        {fields.map((field) => {
                          const fieldKey = `${sectionKey}.${field.name}`;
                          const isHighlighted = riskDetail.highlight_fields?.includes(fieldKey);
                          return (
                            <TableRow
                              key={fieldKey}
                              sx={isHighlighted ? { backgroundColor: 'rgba(244,67,54,0.08)' } : undefined}
                            >
                              <TableCell sx={{ width: 260 }}>
                                <Typography variant="caption" sx={isHighlighted ? { color: 'error.main', fontWeight: 600 } : undefined}>
                                  {field.label}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={isHighlighted ? { color: 'error.main', fontWeight: 600 } : undefined}>
                                  {String(field.value)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              ))}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No risk details available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRiskDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
